import { ATLAS_EXTRUSION, EMPTY_LATE_PACKER, LIBRARY_PAGES_MAX, PAGE_SIZE, placeLateFrames, type AssetManifest, type FrameRect, type LatePacker } from "daedalus-core";
import { pageTextureKey } from "./atlas.ts";

/**
 * The tab's late art (ADR-129 decision 3.7, ADR-130 decisions 2.1, 2.3 and 2.5): a list of pages of its own beside
 * the atlas the scene loaded, each a `DynamicTexture`, filled as sheets arrive. A sheet's frames are stamped by a
 * draw into the page that has room (`placeLateFrames`: a full page opens the next, up to `LIBRARY_PAGES_MAX`), and each
 * frame's rect is added to the manifest the views read, so a prop, a cell or the placement ghost draws it with no
 * view changed. A page is a page of the manifest like any other (`pageTextureKey`, file `late-<n>`).
 *
 * A `DynamicTexture` is empty after a lost WebGL context (P1.6-T236), so every sheet is kept as the encoded PNG it
 * arrived as, a hundredth of the pixels, and when the renderer says `restorewebgl` the sheets are decoded and stamped
 * again in the order they were first, then the owner is told to paint its chunks and props again. The panels draw
 * frames with a 2D canvas, which a `DynamicTexture` cannot give them, so a page's texture answers `getSourceImage` with
 * a 2D canvas that the same stamps are drawn into: every reader of a page's pixels (the panels, the frame masks, the
 * solid masks, the map pictures) reads a late page as it reads a loaded one. The canvas of a page is made when a
 * reader first asks for it, from the decoded sheets the page keeps, and is drawn into by every stamp after that, so a
 * page nobody reads holds no canvas (P1.6-T286: a canvas is 16 MiB beside the texture's 16 MiB).
 */

/**
 * A decoded sheet: what a stamp reads from. An `HTMLImageElement` in a browser.
 */
export interface LateImage {
  readonly width: number;
  readonly height: number;
}

/**
 * A 2D canvas of one page that the panels read, and how a stamp is drawn into it.
 */
export interface LateMirror {
  readonly canvas: unknown;
  draw(image: LateImage, sx: number, sy: number, width: number, height: number, dx: number, dy: number): void;
}

/**
 * What the pages are made over.
 */
export interface LatePagesOptions {
  readonly scene: Phaser.Scene;
  /**
   * The manifest the views read. Its pages, frames and animations are added to; nothing already in it is moved.
   */
  readonly manifest: AssetManifest;
  /**
   * Decodes an encoded PNG into the image a stamp reads. An `HTMLImageElement` from the bytes unless a test gives
   * another.
   */
  readonly decode?: (bytes: Uint8Array) => Promise<LateImage>;
  /**
   * Makes the 2D canvas of a page; null for a host with none, when `imageOf` answers null.
   */
  readonly mirror?: (size: number) => LateMirror | null;
  /**
   * Told once the sheets are stamped again after a restored WebGL context.
   */
  readonly restamped?: () => void;
  /**
   * Told after every `add` that placed a frame or stamped over a held rect, once its pages are drawn, with what the
   * call did. A view that drew the box for a frame it lacked, or a chunk baked from the old art of a corrected one,
   * is painted again from here, whether or not the library also grew.
   */
  readonly arrived?: (added: LateAdded) => void;
  /**
   * Told once, the first time a frame was refused because every page is open and full.
   */
  readonly full?: () => void;
  /** Told once when the context was lost twice inside a minute and the ceiling fell to the pages open. */
  readonly restoresCapped?: () => void;
  /**
   * Waits until the page has drawn a frame. `add` calls it between the steps of an arrival (a page opened, a sheet stamped)
   * once `PACE_SLICE_MS` of work has gone by, so an arrival of many sheets holds no frame for more than one step. The next
   * animation frame (or a short timer where none is drawn) unless a test gives another.
   */
  readonly pace?: () => Promise<void>;
  readonly pageSize?: number;
  readonly maxPages?: number;
  readonly now?: () => number;
  /** Whether a page whose 2D canvas cannot be made is refused (the browser) or drawn without one (a test). */
  readonly mirrorRequired?: boolean;
}

/**
 * What one `add` did, by frame id.
 */
export interface LateAdded {
  readonly placed: readonly string[];
  /**
   * The placed frames the manifest already held before the call (an atlas frame that was replaced, or a frame placed
   * again at another size), whose old art a view may have drawn.
   */
  readonly replaced: readonly string[];
  readonly restamped: readonly string[];
  readonly skipped: readonly string[];
  readonly refused: readonly string[];
}

/**
 * One stamp kept for a repaint: a frame's padded rect on a sheet and where it went on a late page.
 */
interface Stamp {
  readonly sx: number;
  readonly sy: number;
  readonly width: number;
  readonly height: number;
  readonly page: number;
  readonly dx: number;
  readonly dy: number;
}

/**
 * A sheet as it arrived, with every stamp made from it, in the order the stamps were made.
 */
interface KeptSheet {
  readonly bytes: Uint8Array;
  readonly image: LateImage;
  readonly stamps: Stamp[];
}

/**
 * One late page: its texture's key, its place in the manifest and its 2D canvas, null until a reader asks for it.
 */
interface Page {
  readonly key: string;
  readonly manifestPage: number;
  readonly group: string;
  mirror: LateMirror | null;
  readAt: number;
  released: boolean;
}

/**
 * Where a late frame lies: its late page and its inner rect.
 */
interface Held {
  readonly group: string;
  readonly page: number;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /** The size of the slot the frame lies in, when it is larger than the frame (a slot a smaller frame took over). */
  readonly slotW?: number;
  readonly slotH?: number;
}

const RESTORE_WEBGL = "restorewebgl";

/**
 * The most work, in milliseconds, an arrival does between two waits for a frame. A step that cannot be split (a sheet's
 * upload, a page's allocation) runs whole, so a frame is held for about one step and not for a whole arrival.
 */
export const PACE_SLICE_MS = 16;

/**
 * The longest `framePace` waits for a frame, in milliseconds: a hidden tab draws none, and the arrival must still end.
 */
const PACE_FALLBACK_MS = 250;

/**
 * The most stamps of one sheet drawn before the pages are rendered and the arrival waits for a frame (when a slice of work
 * has gone by): a sheet of hundreds of frames is drawn in several steps, none of which holds a frame for long.
 */
export const STAMPS_PER_STEP = 32;

/**
 * Waits until the page has drawn a frame and the task after it has begun, or `PACE_FALLBACK_MS`. It does not wait where
 * there is no animation frame to wait for or the tab is hidden, which draws none and would make each step cost the whole
 * fallback.
 *
 * @returns A promise that resolves once.
 */
function framePace(): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    /**
     * Resolves the wait, once.
     */
    const finish = (): void => {
      if (!done) {
        done = true;
        resolve();
      }
    };
    if (typeof requestAnimationFrame !== "function" || (typeof document !== "undefined" && document.hidden)) {
      setTimeout(finish, 0);
      return;
    }
    setTimeout(finish, PACE_FALLBACK_MS);
    requestAnimationFrame(() => setTimeout(finish, 0));
  });
}

/**
 * The group the pages of the world's own art belong to.
 */
export const MAIN_GROUP = "main";

const NO_REPLACING: ReadonlySet<string> = new Set();

/**
 * A second lost context inside this window means the device cannot hold the pages it has: the ceiling falls to the pages
 * open and the tab says so, which ends a loop of restoring pages and losing the context again.
 */
const RESTORE_WINDOW_MS = 60_000;
const RESTORES_BEFORE_CAP = 2;

/**
 * What the pages read off the scene's renderer: its events.
 */
interface ContextHost {
  on(event: string, listener: () => void): unknown;
  off(event: string, listener: () => void): unknown;
}

/**
 * The renderer of a scene, as far as the pages need it.
 *
 * @param scene The scene.
 * @returns The renderer, or null when it has no events to listen to.
 */
function contextHostOf(scene: Phaser.Scene): ContextHost | null {
  const renderer = (scene as unknown as { readonly sys?: { readonly renderer?: ContextHost } }).sys?.renderer;
  return renderer !== undefined && typeof renderer.on === "function" ? renderer : null;
}

/**
 * Decodes an encoded PNG into an image element.
 *
 * @param bytes The PNG.
 * @returns The decoded image.
 * @throws Error when the bytes are not an image the browser decodes.
 */
async function decodeInBrowser(bytes: Uint8Array): Promise<LateImage> {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "image/png" }));
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Makes a page's 2D canvas in a browser.
 *
 * @param size The page's width and height.
 * @returns The canvas and its draw, or null when the document cannot make one.
 */
function mirrorInBrowser(size: number): LateMirror | null {
  if (typeof document === "undefined") {
    return null;
  }
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (context === null) {
    return null;
  }
  return {
    canvas,
    draw(image, sx, sy, width, height, dx, dy) {
      context.drawImage(image as unknown as CanvasImageSource, sx, sy, width, height, dx, dy, width, height);
    },
  };
}

/**
 * Thrown by a page whose 2D canvas could not be made, which clicks and panels read the page's pixels from.
 */
class MirrorFailure extends Error {}

/**
 * The late pages of one scene.
 */
export class LatePages {
  private readonly scene: Phaser.Scene;
  private readonly manifest: AssetManifest;
  private readonly decode: (bytes: Uint8Array) => Promise<LateImage>;
  private readonly makeMirror: (size: number) => LateMirror | null;
  private readonly restamped: () => void;
  private readonly arrived: (added: LateAdded) => void;
  private readonly full: () => void;
  private readonly restoresCapped: () => void;
  private readonly pageSize: number;
  private maxPages: number;
  private readonly mirrorRequired: boolean;
  private readonly now: () => number;
  private readonly pace: () => Promise<void>;
  private gone = false;
  private restores: number[] = [];
  private readonly packers = new Map<string, LatePacker>();
  private readonly groupPages = new Map<string, number[]>();
  private readonly pages: Page[] = [];
  private probed = false;
  private readonly held = new Map<string, Held>();
  /** Slots a replaced frame left when it was placed again at another size, offered to the next frame that fits. */
  private spare: Held[] = [];
  private readonly sheets: KeptSheet[] = [];
  private told = false;
  private queue: Promise<unknown> = Promise.resolve();
  private listening: ContextHost | null = null;
  private serial = 0;

  /**
   * Lowers the ceiling to the pages open and tells the owner once, so no page opens that the device may not hold: used
   * when a page's 2D canvas cannot be made and when the context is lost twice in a minute.
   */
  private capAtOpen(tell: () => void = this.full): void {
    this.maxPages = this.open;
    if (!this.told) {
      this.told = true;
      tell();
    }
  }

  /**
   * Stamps the kept sheets again after a restored context, in order, and tells the owner. An error from one sheet
   * is logged and the rest are still stamped, since this runs inside the renderer's own restore.
   */
  private readonly onRestore = (): void => {
    const at = this.now();
    this.restores = [...this.restores.filter((earlier) => at - earlier < RESTORE_WINDOW_MS), at];
    if (this.restores.length >= RESTORES_BEFORE_CAP) {
      this.capAtOpen(this.restoresCapped);
    }
    this.queue = this.queue.then(async () => {
      for (const sheet of this.sheets) {
        try {
          const image = await this.decode(sheet.bytes);
          await this.stamp(image, sheet.stamps, false, async () => true, null);
        } catch (error) {
          console.error(`a late sheet could not be stamped again: ${error instanceof Error ? error.message : String(error)}`);
        }
      }
      this.restamped();
    });
  };

  /**
   * @param options The scene, the manifest and the hooks.
   */
  public constructor(options: LatePagesOptions) {
    this.scene = options.scene;
    this.manifest = options.manifest;
    this.decode = options.decode ?? decodeInBrowser;
    this.makeMirror = options.mirror ?? mirrorInBrowser;
    this.restamped = options.restamped ?? (() => undefined);
    this.arrived = options.arrived ?? (() => undefined);
    this.full = options.full ?? (() => undefined);
    this.restoresCapped = options.restoresCapped ?? this.full;
    this.pageSize = options.pageSize ?? PAGE_SIZE;
    this.maxPages = options.maxPages ?? LIBRARY_PAGES_MAX;
    this.now = options.now ?? Date.now;
    this.pace = options.pace ?? framePace;
    this.mirrorRequired = options.mirrorRequired ?? false;
  }

  /**
   * How many late pages are open.
   *
   * @returns The count.
   */
  public get open(): number {
    return this.pages.filter((page) => !page.released).length;
  }

  /**
   * The memory the decoded sheets kept for a repaint hold, in bytes (four a pixel).
   *
   * @returns The bytes.
   */
  public get keptBytes(): number {
    return this.sheets.reduce((sum, sheet) => sum + sheet.image.width * sheet.image.height * 4, 0);
  }

  /**
   * How many pages hold a 2D canvas: the ones a reader has asked for.
   *
   * @returns The count.
   */
  public get mirrors(): number {
    return this.pages.filter((page) => page.mirror !== null && !page.released).length;
  }

  /**
   * Lets go of the 2D canvas of every page no reader has asked for within a time, which frees its 16 MiB; the next
   * reader's ask makes it again from the kept sheets. A canvas a reader is holding is not changed, it only stops
   * receiving stamps, so a reader asks for the canvas each time it draws rather than keeping it.
   *
   * @param idleMs How long a page's canvas may go unasked.
   * @returns How many canvases were let go.
   */
  public releaseIdle(idleMs: number): number {
    const at = this.now();
    let released = 0;
    for (const page of this.pages) {
      if (page.mirror !== null && at - page.readAt > idleMs) {
        page.mirror = null;
        released += 1;
      }
    }
    return released;
  }

  /**
   * The 2D canvas of a page of the manifest, for the panels.
   *
   * @param page A page index of the manifest.
   * @returns The canvas of a late page, or null for any other page or a host that makes none.
   */
  public imageOf(page: number): HTMLCanvasElement | null {
    const found = this.pages.find((each) => each.manifestPage === page && !each.released);
    return found === undefined ? null : ((this.mirrorOf(found)?.canvas as HTMLCanvasElement | undefined) ?? null);
  }

  /**
   * Whether a frame lies on a late page.
   *
   * @param id The frame id.
   * @returns True when it does.
   */
  public holds(id: string): boolean {
    return this.held.has(id);
  }

  /**
   * The group of the late page a frame lies on.
   *
   * @param id The frame id.
   * @returns The group, or undefined when the frame lies on no late page.
   */
  public groupOf(id: string): string | undefined {
    return this.held.get(id)?.group;
  }

  /**
   * Stamps the frames of arrived sheets into late pages and adds them to the manifest. A frame the manifest holds
   * from the atlas is left as it is. A frame already on a late page of the same size is stamped over its own rect and
   * its fields are replaced; one of another size is placed again, and the old rect stays until the page is dropped.
   * Calls are served one at a time, in the order made, so two arrivals never place a frame on the same pixels.
   *
   * @param sheets The encoded PNG of each sheet of `assets`, in the order of its pages.
   * @param assets The manifest the route answered: its pages are the sheets, its frames the rects on them.
   * @param replace Ids, frames or strips, that win over what the manifest holds from the atlas: such a frame is placed
   *   on a late page and its rect in the manifest rewritten, and such a strip is set over the manifest's.
   * @param group The group the frames belong to (P1.6-T286): each group fills pages of its own, so a group can be released
   *   whole (`releaseGroup`) without a page that holds a frame another group needs. A frame held by another group is placed
   *   again on a page of this one and is then this group's.
   * @returns What was done to each frame.
   * @throws Error when a sheet cannot be decoded or a page's texture cannot be made.
   */
  public add(sheets: readonly Uint8Array[], assets: AssetManifest, replace: ReadonlySet<string> = NO_REPLACING, group: string = MAIN_GROUP): Promise<LateAdded> {
    const run = this.queue.then(() => this.place(sheets, assets, replace, group));
    this.queue = run.catch(() => undefined);
    return run;
  }

  /**
   * Settles when every arrival asked for so far has been placed and stamped, and every repaint after a restored
   * context has been made.
   *
   * @returns A promise that does not reject.
   */
  public settled(): Promise<void> {
    return this.queue.then(() => undefined);
  }

  /**
   * Stops listening to the renderer and removes the pages' textures. The manifest keeps what was added to it.
   */
  public destroy(): void {
    this.gone = true;
    this.listening?.off(RESTORE_WEBGL, this.onRestore);
    this.listening = null;
    for (const page of this.pages) {
      if (!page.released) {
        this.scene.textures.remove(page.key);
      }
    }
    this.pages.length = 0;
    this.packers.clear();
    this.groupPages.clear();
    this.held.clear();
    this.sheets.length = 0;
  }

  /**
   * Places and stamps one arrival. When a stamp throws part way (a lost context, a texture that cannot be read) the call
   * rejects with nothing placed: no frame of the arrival is held or written to the manifest, the group's packer has
   * advanced past the slots it planned (they stay empty), and the sheets stamped so far stay kept, with the stamps made,
   * for a repaint after a restored context. A later call places the frames again.
   *
   * @param sheets The encoded sheets.
   * @param assets The manifest the route answered.
   * @param replace The ids that win over the atlas's.
   * @param group The group the frames belong to.
   * @returns What was done.
   */
  private async place(sheets: readonly Uint8Array[], assets: AssetManifest, replace: ReadonlySet<string>, group: string): Promise<LateAdded> {
    if (sheets.length !== assets.pages.length) {
      throw new Error(`${sheets.length} sheets arrived for ${assets.pages.length} pages of late frames`);
    }
    this.listen();
    const frames = this.manifest.frames as Record<string, FrameRect>;
    const placed: string[] = [];
    const replaced: string[] = [];
    const restamped: string[] = [];
    const skipped: string[] = [];
    const refused: string[] = [];
    const fresh: { id: string; rect: FrameRect }[] = [];
    const again: { id: string; rect: FrameRect; at: Held }[] = [];
    for (const id of Object.keys(assets.frames)) {
      const rect = assets.frames[id] as FrameRect;
      const at = this.held.get(id);
      if ((at === undefined && frames[id] !== undefined && !replace.has(id)) || (at !== undefined && at.group !== group && group !== MAIN_GROUP)) {
        skipped.push(id);
      } else if (at !== undefined && at.group === group && at.w === rect.w && at.h === rect.h) {
        again.push({ id, rect, at });
      } else {
        fresh.push({ id, rect });
      }
    }
    const reused = new Map<string, Held>();
    const leaving = new Map<string, Held>();
    for (const { id, rect } of fresh) {
      const old = this.held.get(id);
      const own = old === undefined || old.group !== group ? [] : [{ ...old, w: old.slotW ?? old.w, h: old.slotH ?? old.h }];
      const fits = [...this.spare.filter((slot) => slot.group === group), ...own].filter((slot) => slot.w >= rect.w && slot.h >= rect.h).sort((a, b) => a.w * a.h - b.w * b.h)[0];
      if (fits !== undefined) {
        const at = this.spare.indexOf(fits);
        if (at >= 0) {
          this.spare.splice(at, 1);
        }
        reused.set(id, fits);
      }
      if (old !== undefined && old.group === group && fits !== own[0]) {
        leaving.set(id, own[0] as Held);
      }
    }
    const planned = placeLateFrames(this.packerOf(group), fresh.filter(({ id }) => !reused.has(id)).map(({ id, rect }) => ({ id, w: rect.w, h: rect.h })), this.pageSize, this.maxPages - (this.open - this.openIn(group)));
    const where = new Map<string, { page: number; x: number; y: number; slotW?: number; slotH?: number }>();
    for (const [id, slot] of reused) {
      where.set(id, { page: slot.page, x: slot.x, y: slot.y, slotW: slot.w, slotH: slot.h });
    }
    for (const refusal of planned.refused) {
      refused.push(refusal.id);
    }
    if (planned.refused.some((refusal) => refusal.reason === "pages") && !this.told) {
      this.told = true;
      this.full();
    }
    const images: LateImage[] = [];
    for (const sheet of sheets) {
      images.push(await this.decode(sheet));
    }
    let sliceStart = this.now();
    const breathe = async (): Promise<boolean> => {
      if (this.now() - sliceStart >= PACE_SLICE_MS) {
        await this.pace();
        sliceStart = this.now();
      }
      return !this.gone;
    };
    const ended: LateAdded = { placed: [], replaced: [], restamped: [], skipped, refused };
    try {
      for (let local = 0; local < planned.packer.pages; local += 1) {
        this.ensureGroupPage(group, local);
        if (!(await breathe())) {
          return ended;
        }
      }
    } catch (error) {
      if (!(error instanceof MirrorFailure)) {
        throw error;
      }
      this.capAtOpen();
      return this.place(sheets, assets, replace, group);
    }
    const globals = this.groupPages.get(group) ?? [];
    for (const placement of planned.placed) {
      where.set(placement.id, { page: globals[placement.page] as number, x: placement.x, y: placement.y });
    }
    const stamps: Stamp[][] = sheets.map(() => []);
    const arriving = new Map<string, Held>();
    const take = (id: string, rect: FrameRect, page: number, x: number, y: number, slotW?: number, slotH?: number): void => {
      (stamps[rect.page] as Stamp[]).push({ sx: rect.x - ATLAS_EXTRUSION, sy: rect.y - ATLAS_EXTRUSION, width: rect.w + 2 * ATLAS_EXTRUSION, height: rect.h + 2 * ATLAS_EXTRUSION, page, dx: x - ATLAS_EXTRUSION, dy: y - ATLAS_EXTRUSION });
      arriving.set(id, { group, page, x, y, w: rect.w, h: rect.h, ...(slotW === undefined || slotH === undefined ? {} : { slotW, slotH }) });
    };
    for (const { id, rect, at } of again) {
      take(id, rect, at.page, at.x, at.y, at.slotW, at.slotH);
    }
    for (const { id, rect } of fresh) {
      const placement = where.get(id);
      if (placement !== undefined) {
        take(id, rect, placement.page, placement.x, placement.y, placement.slotW, placement.slotH);
        const left = leaving.get(id);
        if (left !== undefined) {
          this.spare.push(left);
        }
      }
    }
    this.packers.set(group, planned.packer);
    for (let index = 0; index < sheets.length; index += 1) {
      const made = stamps[index] as Stamp[];
      if (made.length === 0) {
        continue;
      }
      const kept: KeptSheet = { bytes: sheets[index] as Uint8Array, image: images[index] as LateImage, stamps: [] };
      this.sheets.push(kept);
      if (!(await this.stamp(images[index] as LateImage, made, true, breathe, kept.stamps)) || !(await breathe())) {
        return ended;
      }
    }
    for (const [id, at] of arriving) {
      this.held.set(id, at);
    }
    const stampedOver = new Set(again.map((each) => each.id));
    const known = [...stampedOver, ...fresh.filter((each) => where.has(each.id)).map((each) => each.id)];
    for (const id of known) {
      const rect = assets.frames[id] as FrameRect;
      const at = this.held.get(id) as Held;
      const page = this.pages[at.page] as { key: string; manifestPage: number };
      const before = frames[id] !== undefined;
      frames[id] = { ...rect, page: page.manifestPage, x: at.x, y: at.y };
      if (stampedOver.has(id)) {
        restamped.push(id);
        continue;
      }
      if (before) {
        replaced.push(id);
      }
      const texture = this.scene.textures.get(page.key);
      if (texture.has(id)) {
        texture.remove(id);
      }
      texture.add(id, 0, at.x, at.y, at.w, at.h);
      placed.push(id);
    }
    const animations = this.manifest.animations as Record<string, AssetManifest["animations"][string]>;
    for (const id of Object.keys(assets.animations)) {
      if ((animations[id] === undefined || replace.has(id)) && assets.animations[id]?.frames.every((shown) => frames[shown] !== undefined) === true) {
        animations[id] = assets.animations[id] as AssetManifest["animations"][string];
      }
    }
    const added = { placed, replaced, restamped, skipped, refused };
    if (placed.length > 0 || restamped.length > 0) {
      this.arrived(added);
    }
    return added;
  }

  /**
   * The packer of a group: the shelf its next frame goes on, with no page open for a group that has none.
   *
   * @param group The group.
   * @returns The packer.
   */
  private packerOf(group: string): LatePacker {
    return this.packers.get(group) ?? EMPTY_LATE_PACKER;
  }

  /**
   * How many pages of a group are open.
   *
   * @param group The group.
   * @returns The count.
   */
  public openIn(group: string): number {
    return this.pages.filter((page) => page.group === group && !page.released).length;
  }

  /**
   * Opens the page a group's packer numbered `local`, when it is not open: its texture and its place in the manifest's
   * page list. Pages stand in one list for every group, and a released page keeps its place in it until a page opened later takes it, so a page number in
   * the manifest names the same page for as long as the manifest holds a frame on it.
   *
   * @param group The group.
   * @param local The page's number in the group's packer.
   * @throws Error when the texture cannot be made.
   */
  private ensureGroupPage(group: string, local: number): void {
    const numbers = this.groupPages.get(group) ?? [];
    this.groupPages.set(group, numbers);
    if (numbers[local] !== undefined) {
      return;
    }
    const pages = this.manifest.pages as { file: string; width: number; height: number }[];
    const free = this.pages.findIndex((each) => each.released);
    const index = free >= 0 ? free : this.pages.length;
    const manifestPage = free >= 0 ? (this.pages[free] as Page).manifestPage : pages.length;
    const fresh = free < 0;
    if (fresh) {
      pages.push({ file: `late-${index}`, width: this.pageSize, height: this.pageSize });
    }
    const key = pageTextureKey(this.manifest, manifestPage);
    const texture = this.scene.textures.addDynamicTexture(key, this.pageSize, this.pageSize);
    if (texture === null) {
      if (fresh) {
        pages.pop();
      }
      throw new Error(`could not create the late page texture ${key}`);
    }
    if (this.mirrorRequired && !this.probed) {
      if (this.makeMirror(1) === null) {
        this.scene.textures.remove(key);
        if (fresh) {
          pages.pop();
        }
        throw new MirrorFailure(`could not make the 2D canvas of the late page ${key}`);
      }
      this.probed = true;
    }
    const page: Page = { key, manifestPage, group, mirror: null, readAt: this.now(), released: false };
    (texture as unknown as { getSourceImage: () => unknown }).getSourceImage = () => this.mirrorOf(page)?.canvas ?? null;
    this.pages[index] = page;
    numbers[local] = index;
  }

  /**
   * Lets go of a group (P1.6-T286): the frames it still holds leave the manifest, with the strips that show them, its
   * pages leave the scene, and the sheets it kept for a lost context are dropped. A frame another group took over is not
   * the group's any more and stays. The group's next arrival opens new pages, which take the places released pages left
   * (in the page list and in the manifest's), so neither list grows with each release. The release is served in its
   * turn with the arrivals, so an arrival that is decoding lands before the group goes.
   *
   * @param group The group.
   * @returns The ids of the frames that left the manifest.
   */
  public releaseGroup(group: string): Promise<readonly string[]> {
    const run = this.queue.then(() => this.releaseNow(group));
    this.queue = run.then(() => undefined, () => undefined);
    return run;
  }

  /**
   * Lets go of a group at once, between two arrivals.
   *
   * @param group The group.
   * @returns The ids of the frames that left the manifest.
   */
  private releaseNow(group: string): readonly string[] {
    const frames = this.manifest.frames as Record<string, FrameRect>;
    const gone: string[] = [];
    for (const [id, at] of [...this.held]) {
      if (at.group === group) {
        this.held.delete(id);
        delete frames[id];
        gone.push(id);
      }
    }
    const leaving = new Set(gone);
    const animations = this.manifest.animations as Record<string, AssetManifest["animations"][string]>;
    for (const id of Object.keys(animations)) {
      if (animations[id]?.frames.some((shown) => leaving.has(shown)) === true) {
        delete animations[id];
      }
    }
    const closed = new Set<number>();
    this.pages.forEach((page, index) => {
      if (page.group === group && !page.released) {
        page.released = true;
        page.mirror = null;
        this.scene.textures.remove(page.key);
        closed.add(index);
      }
    });
    this.spare = this.spare.filter((slot) => !closed.has(slot.page));
    const kept = this.sheets.map((sheet) => ({ bytes: sheet.bytes, image: sheet.image, stamps: sheet.stamps.filter((each) => !closed.has(each.page)) })).filter((sheet) => sheet.stamps.length > 0);
    this.sheets.length = 0;
    this.sheets.push(...kept);
    this.packers.delete(group);
    this.groupPages.delete(group);
    return gone;
  }

  /**
   * The 2D canvas of a page, made when first asked for: every stamp the page holds is drawn into it again from the
   * decoded sheets, in the order they were first, so it equals what the page's texture holds.
   *
   * @param page The page.
   * @returns The canvas, or null when the host makes none.
   */
  private mirrorOf(page: Page): LateMirror | null {
    page.readAt = this.now();
    if (page.mirror !== null) {
      return page.mirror;
    }
    const mirror = this.makeMirror(this.pageSize);
    if (mirror === null) {
      return null;
    }
    const index = this.pages.indexOf(page);
    for (const sheet of this.sheets) {
      for (const each of sheet.stamps) {
        if (each.page === index) {
          mirror.draw(sheet.image, each.sx, each.sy, each.width, each.height, each.dx, each.dy);
        }
      }
    }
    page.mirror = mirror;
    return mirror;
  }

  /**
   * Draws stamps of one decoded sheet into the pages' textures, and, when asked, into their 2D canvases, in steps of
   * `STAMPS_PER_STEP`: the pages a step touched are rendered at its end, and `breathe` is asked whether to go on, which
   * is where the arrival waits for a frame. A stamp is recorded once it is drawn, so a 2D canvas made between two steps is
   * drawn again from exactly the stamps already made, and the steps after it draw into it.
   *
   * @param image The decoded sheet.
   * @param stamps The stamps to draw.
   * @param mirrored True to draw into the 2D canvases too: a repaint after a lost context leaves them as they are.
   * @param breathe Waits for a frame when a slice of work is spent; false when the tab was destroyed meanwhile.
   * @param recorded Where each stamp is recorded as it is drawn, or null to record none.
   * @returns False when the tab was destroyed before every stamp was drawn.
   */
  private async stamp(image: LateImage, stamps: readonly Stamp[], mirrored: boolean, breathe: () => Promise<boolean>, recorded: Stamp[] | null): Promise<boolean> {
    const source = `late-source:${this.serial}`;
    this.serial += 1;
    const sheet = this.scene.textures.addImage(source, image as unknown as HTMLImageElement);
    if (sheet === null) {
      throw new Error(`could not read the late sheet ${source}`);
    }
    try {
      for (let from = 0; from < stamps.length; from += STAMPS_PER_STEP) {
        const touched = new Set<number>();
        for (let index = from; index < Math.min(stamps.length, from + STAMPS_PER_STEP); index += 1) {
          const each = stamps[index] as Stamp;
          const name = `s${index}`;
          sheet.add(name, 0, each.sx, each.sy, each.width, each.height);
          const page = this.pages[each.page] as Page;
          (this.scene.textures.get(page.key) as unknown as StampTarget).stamp(source, name, each.dx, each.dy, { originX: 0, originY: 0 });
          touched.add(each.page);
          if (mirrored) {
            page.mirror?.draw(image, each.sx, each.sy, each.width, each.height, each.dx, each.dy);
          }
          recorded?.push(each);
        }
        for (const page of touched) {
          (this.scene.textures.get((this.pages[page] as { key: string }).key) as unknown as StampTarget).render();
        }
        if (from + STAMPS_PER_STEP < stamps.length && !(await breathe())) {
          return false;
        }
      }
      return true;
    } finally {
      this.scene.textures.remove(source);
    }
  }

  /**
   * Starts listening to the renderer for a restored context, once.
   */
  private listen(): void {
    if (this.listening !== null) {
      return;
    }
    const host = contextHostOf(this.scene);
    if (host === null) {
      return;
    }
    host.on(RESTORE_WEBGL, this.onRestore);
    this.listening = host;
  }
}

/**
 * The part of a dynamic texture a stamp uses.
 */
interface StampTarget {
  stamp(key: string, frame: string, x: number, y: number, config: { readonly originX: number; readonly originY: number }): unknown;
  render(): unknown;
}
